import { startOperation } from '../telemetry/client';
import { useTranslation } from '../../i18n/react';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { detectImageFormat, imageFormats, IMAGE_ACCEPT, validateBatch } from './formats';
import { validateResizeOptions, type ResizeAxis, type ResizeOptions, type ResizeOutputFormat } from './resize';
import type { ConversionProgress } from './media';
import './converter.css';

export function ImageResizeCard() {
  const tr = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const [files, setFiles] = useState<File[]>([]);
  const [width, setWidth] = useState('1000');
  const [height, setHeight] = useState('1000');
  const [proportional, setProportional] = useState(false);
  const [axis, setAxis] = useState<ResizeAxis>('width');
  const [size, setSize] = useState('1000');
  const [lastAxis, setLastAxis] = useState<ResizeAxis>('width');
  const [output, setOutput] = useState<ResizeOutputFormat>('png');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);
  const [progress, setProgress] = useState<ConversionProgress>({ done: 0, total: 0 });
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);

  function options(): ResizeOptions {
    const result: ResizeOptions = proportional
      ? { mode: 'proportional', axis, size: Number(size) }
      : { mode: 'canvas', width: Number(width), height: Number(height) };
    validateResizeOptions(result);
    return result;
  }
  function changeDimension(edge: ResizeAxis, value: string) {
    if (proportional) { setAxis(edge); setSize(value); }
    else if (edge === 'width') setWidth(value);
    else setHeight(value);
    setLastAxis(edge); setInvalidSize(false); setFeedback(null);
  }
  function toggleProportional(enabled: boolean) {
    if (enabled) { setAxis(lastAxis); setSize(lastAxis === 'width' ? width : height); }
    setProportional(enabled); setInvalidSize(false); setFeedback(null);
  }
  function checkSize() {
    try { options(); setInvalidSize(false); }
    catch (error) { setInvalidSize(true); setFeedback({ tone: 'error', message: (error as Error).message }); }
  }
  function addFiles(incoming: File[]) {
    if (busy || !incoming.length) return;
    try {
      const next = [...files, ...incoming];
      validateBatch(next);
      setFiles(next); setFeedback(null);
    } catch (error) { setFeedback({ tone: 'error', message: (error as Error).message }); }
  }
  async function download() {
    if (controller.current) return;
    let settings: ResizeOptions;
    try { settings = options(); setInvalidSize(false); }
    catch (error) { setInvalidSize(true); setFeedback({ tone: 'error', message: (error as Error).message }); return; }
    try { validateBatch(files); }
    catch (error) { setFeedback({ tone: 'error', message: (error as Error).message }); return; }
    const operation = startOperation('image-resize');
    const abort = new AbortController();
    controller.current = abort; setBusy(true); setFeedback(null);
    setProgress({ done: 0, total: files.length });
    try {
      const { resizeImages, downloadImageResult } = await import('./media');
      const result = await resizeImages(files, settings, output, abort.signal, state => { if (mounted.current) setProgress(state); });
      if (!mounted.current || abort.signal.aborted) return;
      operation.complete({ exportBytes: result.blob.size });
      downloadImageResult(result);
      setFeedback({ tone: 'success', message: result.count > 1 ? 'Đã tạo ZIP chứa ' + result.count + ' ảnh.' : 'Đã tạo ' + result.name });
    } catch (error) {
      if (mounted.current) setFeedback({ tone: 'error', message: error instanceof Error ? error.message : 'Không thể đổi kích thước ảnh.' });
    } finally { controller.current = null; if (mounted.current) setBusy(false); }
  }

  const note = proportional
    ? 'Nhập Rộng hoặc Cao · cạnh còn lại tự tính theo từng ảnh.'
    : output === 'jpg' ? 'Giữ tỷ lệ, căn giữa · JPG thêm nền trắng.' : 'Giữ tỷ lệ, căn giữa · nhiều ảnh tải một ZIP.';
  const status = busy ? progress.packing ? 'Đang nén ZIP…' : tr('Đang resize ' + progress.done + '/' + progress.total) + ' ảnh…' : feedback?.message || note;
  const animated = files.some(file => ['gif','tiff','webp','avif','heic'].includes(detectImageFormat(file) || ''));
  return <article className="image-converter-card image-resize-card" aria-label="Resize image(s)" aria-busy={busy}>
    <h3>Resize image(s)</h3>
    <input ref={input} className="visually-hidden" type="file" multiple aria-label={tr("Chọn ảnh để đổi kích thước")} disabled={busy} accept={IMAGE_ACCEPT}
      onChange={event => { addFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
    <div className={'image-converter-dropzone' + (dragging ? ' is-dragging' : '')} onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }} onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={event => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}>
      {files.length ? <>
        <ol className="image-converter-list" aria-label={tr("Ảnh chờ resize")}>{files.map((file, index) => <li key={index + '-' + file.name + '-' + file.lastModified}>
          <span className="image-converter-number">{index + 1}</span><Icon name="image" size={16}/>
          <span className="image-converter-file" title={file.name}>{file.name}<small>{tr(imageFormats.find(format => format.id === detectImageFormat(file))?.label)} · {(file.size / 1024).toFixed(0)} KB</small></span>
          <button type="button" className="image-converter-remove" disabled={busy} aria-label={'Xóa ảnh resize ' + (index + 1) + ': ' + file.name} onClick={() => { setFiles(current => current.filter((_, position) => position !== index)); setFeedback(null); }}><Icon name="close" size={14}/></button>
        </li>)}</ol>
        <div className="image-converter-list-footer"><button type="button" disabled={busy} onClick={() => input.current?.click()}>{tr("+ Thêm ảnh")}</button><span>{files.length}{tr("/50 ảnh")}</span></div>
        {animated && <p className="image-converter-frame-note">{tr("Resize: lấy khung / trang đầu.")}</p>}
      </> : <button type="button" className="image-converter-placeholder" disabled={busy} onClick={() => input.current?.click()}>
        <span className="image-converter-plus" aria-hidden="true">+</span><span>Drag images to resize</span><small>{tr("Đổi kích thước hàng loạt")}<br/>{proportional ? tr('Scale theo một cạnh · không thêm phần dư') : tr('Giữ tỷ lệ · thêm phần dư · không kéo méo')}</small>
      </button>}
    </div>
    <div className="image-converter-controls image-resize-controls">
      <div className="image-resize-dimensions">
        <label>{tr("Rộng (px)")}<input type="number" aria-label={tr("Chiều rộng (px)")} aria-invalid={invalidSize && (!proportional || axis === 'width')} aria-describedby="image-resize-status" min="1" max="32767" step="1" inputMode="numeric" disabled={busy} value={proportional ? axis === 'width' ? size : '' : width} placeholder={proportional ? 'Auto' : undefined} onBlur={checkSize} onChange={event => changeDimension('width', event.target.value)} /></label>
        <label>{tr("Cao (px)")}<input type="number" aria-label={tr("Chiều cao (px)")} aria-invalid={invalidSize && (!proportional || axis === 'height')} aria-describedby="image-resize-status" min="1" max="32767" step="1" inputMode="numeric" disabled={busy} value={proportional ? axis === 'height' ? size : '' : height} placeholder={proportional ? 'Auto' : undefined} onBlur={checkSize} onChange={event => changeDimension('height', event.target.value)} /></label>
        <label>{tr("Định dạng")}<select aria-label={tr("Định dạng ảnh resize")} value={output} disabled={busy} onChange={event => { setOutput(event.target.value as ResizeOutputFormat); setFeedback(null); }}><option value="png">PNG</option><option value="jpg">JPG</option><option value="webp">WebP</option></select></label>
      </div>
      <label className="image-resize-proportional"><input type="checkbox" checked={proportional} disabled={busy} aria-describedby="image-resize-status" onChange={event => toggleProportional(event.target.checked)} />Proportional Scaling</label>
      <button className="image-converter-download" type="button" disabled={!files.length || busy} onClick={() => void download()}>{busy ? progress.packing ? tr('Nén ZIP…') : tr('Đang resize ' + progress.done + '/' + progress.total) : 'Download Image(s)'}</button>
      <p id="image-resize-status" className={'image-converter-status' + (feedback ? ' is-' + feedback.tone : '')} role={feedback?.tone === 'error' ? 'alert' : 'status'} title={tr(status)}>{tr(status)}</p>
    </div>
  </article>;
}
